<script setup lang="ts">
import { computed } from 'vue'
import { RouteLocationRaw } from 'vue-router'
import { SelectOption } from '@/components/input/SelectField.vue'

export type BreadcrumbDropdown = {
  options: SelectOption<string>[]
  activeId?: string
  loading?: boolean
  onSelect: (id: string) => void
}

export type BreadcrumbItem = {
  pageLabel: string
  to?: RouteLocationRaw
  selection?: string
  dropdown?: BreadcrumbDropdown
}

const props = defineProps<{ items: BreadcrumbItem[] }>()

const backTo = computed(() => [...props.items].reverse().find((item) => item.to)?.to)
</script>

<template>
  <nav class="breadcrumb-container" aria-label="Breadcrumb">
    <v-btn
      v-if="backTo"
      density="compact"
      variant="tonal"
      color="primary"
      icon="chevron_left"
      style="margin: -1.2em 0.5em -1em 0"
      :to="backTo"
    />

    <template v-for="(item, index) in items" :key="index">
      <v-icon v-if="index > 0" icon="navigate_next" />

      <span v-if="item.dropdown" class="breadcrumb">
        <v-menu>
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              variant="text"
              density="compact"
              append-icon="arrow_drop_down"
              :loading="item.dropdown.loading"
              class="leaf-selector"
            >
              {{ item.pageLabel }}
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="option in item.dropdown.options"
              :key="option.value"
              :active="option.value === item.dropdown.activeId"
              @click="item.dropdown.onSelect(option.value)"
            >
              <v-list-item-title>{{ option.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </span>

      <span v-else class="breadcrumb">
        <router-link v-if="item.to" :to="item.to">{{ item.pageLabel }}</router-link>
        <span v-else class="current-page">{{ item.pageLabel }}</span>
        <span v-if="item.selection" class="selection text-medium-emphasis">
          ({{ item.selection }})
        </span>
      </span>
    </template>
  </nav>
</template>

<style scoped lang="scss">
.breadcrumb-container {
  z-index: 1000;
  position: sticky;
  top: 4em;
  width: max-content;
  background: rgba(var(--v-theme-background), 0.75);

  .breadcrumb {
    display: inline-block;
    position: relative;
    margin: 0 0.25em;
  }
  .selection {
    margin-left: 0.35em;
  }
  .current-page {
    font-weight: 600;
  }
  .leaf-selector {
    font-weight: 600;
    font-size: 1rem;
    text-transform: none;
    letter-spacing: normal;
  }
}
</style>
